import {
  Component,
  ElementRef,
  HostListener,
  HostBinding,
  Input,
  computed,
  forwardRef,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faCalendar,
} from '@fortawesome/free-solid-svg-icons';
import {
  addMonths,
  format,
  isAfter,
  isBefore,
  isSameMonth,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import {
  NgpDatePickerFirstDayOfWeekNumber,
  NgpDateRangePicker,
} from 'ng-primitives/date-picker';

import {
  SUI_FORM_FIELD_CONTROL,
  SuiFormFieldControl,
} from '../form-field/form-field-control';
import { SuiInputComponent } from '../input';
import { SuiPopoverDirective, SuiPopoverTriggerDirective } from '../popover';
import { SuiFormControlBase } from '../shared/cva-helper';
import { resolveSuiBoundary } from '../shared/boundary';
import { SuiCalendarComponent } from './sui-calendar.component';

/** The range picker's value — both endpoints `startOfDay`-normalised. */
export interface SuiDateRange {
  start: Date | null;
  end: Date | null;
}

/**
 * Two-month date-range picker.
 *
 * Composes **two** independent ng-primitives `[ngpDateRangePicker]` roots (the
 * left month and the next month) that **share one `{ start, end }` value**: both
 * bind the same controlled `startDate`/`endDate`, so a selection in either month
 * updates the single value and both grids re-render + highlight consistently
 * (the range-`between` band spans across months because each cell computes it
 * from the absolute start/end). Binding those inputs makes each root's internal
 * `state.set()` a no-op while it still emits its `*Change` output — the standard
 * controlled round-trip.
 *
 * One **shared nav header** drives a single `focusedMonth` (left = M, right =
 * M+1); the per-root `focusedDate` is controlled too, and each root's
 * `focusedDateChange` only updates its roving position while the date stays in
 * that root's month — so the two-month window never drifts (cross-month moves go
 * through the header). Reuses the shared `<sui-calendar>` (week numbers on).
 *
 * **Two presentations** (mirrors `sui-date-picker`): the default is a **field** —
 * a read-only `sui-input` showing the formatted `"start – end"` text + a
 * calendar-icon trigger opening a `sui-popover` that holds the two-month calendar;
 * picking the second endpoint commits and closes (close-on-complete). `[inline]`
 * renders the calendar directly. Both share one `calendarBody` template; the range
 * roots self-provide their state *inside* it, so the popover-portaled copy needs
 * no declaration-site DI trick (unlike the single picker's outer-root setup).
 */
@Component({
  selector: 'sui-date-range-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FaIconComponent,
    SuiCalendarComponent,
    SuiInputComponent,
    SuiPopoverTriggerDirective,
    SuiPopoverDirective,
    NgpDateRangePicker,
  ],
  templateUrl: './sui-date-range-picker.component.html',
  styleUrl: './sui-date-range-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiDateRangePickerComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiDateRangePickerComponent),
    },
  ],
})
export class SuiDateRangePickerComponent
  extends SuiFormControlBase<SuiDateRange>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-date-range-picker-${++SuiDateRangePickerComponent._idCounter}`;
  private _focused = false;

  private readonly _popoverTrigger = viewChild(SuiPopoverTriggerDirective);

  readonly controlType = 'date-range-picker';

  readonly faPrev = faAngleLeft;
  readonly faNext = faAngleRight;
  readonly faCalendar = faCalendar;

  // First day of the left month; right month is always +1.
  private readonly _focusedMonth = signal(startOfMonth(new Date()));
  // Per-root roving focus positions (kept within their respective months).
  readonly leftFocused = signal<Date>(startOfMonth(new Date()));
  readonly rightFocused = signal<Date>(startOfMonth(addMonths(new Date(), 1)));

  readonly leftLabel = computed(() =>
    format(this._focusedMonth(), 'MMMM yyyy'),
  );
  readonly rightLabel = computed(() =>
    format(addMonths(this._focusedMonth(), 1), 'MMMM yyyy'),
  );

  /** Controlled start/end fed to both roots (`null` → undefined → unset). */
  readonly startInput = computed(() => this.value()?.start ?? undefined);
  readonly endInput = computed(() => this.value()?.end ?? undefined);

  /** Field display text — "start – end" (a placeholder dash for a missing end). */
  readonly inputText = computed(() => {
    const v = this.value();
    if (!v?.start && !v?.end) {
      return '';
    }
    const s = v?.start ? format(v.start, this.displayFormat) : '…';
    const e = v?.end ? format(v.end, this.displayFormat) : '…';
    return `${s} – ${e}`;
  });

  readonly prevDisabled = computed(() => {
    if (this.isDisabled()) {
      return true;
    }
    return !!this.min && !isAfter(this._focusedMonth(), startOfMonth(this.min));
  });
  readonly nextDisabled = computed(() => {
    if (this.isDisabled()) {
      return true;
    }
    const right = startOfMonth(addMonths(this._focusedMonth(), 1));
    return !!this.max && !isBefore(right, startOfMonth(this.max));
  });

  @Input() id = '';
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  /** Predicate disabling individual dates (e.g. weekends). */
  @Input() dateDisabled: (date: Date) => boolean = () => false;
  /** Primitive numbering: 1=Mon … 7=Sun. Defaults to Monday. */
  @Input() firstDayOfWeek: NgpDatePickerFirstDayOfWeekNumber = 1;

  /** Render the two-month calendar directly instead of the field + popover. */
  @Input() inline = false;
  /** Compact density: 26px day squares and a tighter gap between the two months. */
  @Input() @HostBinding('class.sui-date-range-picker--compact') compact = false;
  /** Placeholder for the field input (field mode only). */
  @Input() placeholder = '';
  /** date-fns format for each endpoint in the field text. */
  @Input() displayFormat = 'dd MMM yyyy';
  /** Popover container override; defaults to the nearest boundary pane, else `body`. */
  @Input() popoverContainer?: string | HTMLElement;

  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  protected get resolvedPopoverContainer(): string | HTMLElement {
    return (
      this.popoverContainer ?? resolveSuiBoundary(this.hostEl.nativeElement)
    );
  }

  /**
   * Pin the two-month window to a fixed left month, **decoupled from the value**
   * (the selection still highlights wherever it falls). When set, `writeValue`
   * stops re-framing on the selection — e.g. a consumer that always wants
   * [previous, current] month. The header arrows still navigate.
   */
  @Input() set initialMonth(month: Date | null) {
    this._initialMonthPinned = !!month;
    if (month) {
      this._setFocusedMonth(month);
    }
  }
  private _initialMonthPinned = false;

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get minDate(): Date | undefined {
    return this.min ?? undefined;
  }
  get maxDate(): Date | undefined {
    return this.max ?? undefined;
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }
  get focused(): boolean {
    return this._focused;
  }
  get empty(): boolean {
    return !this.value()?.start && !this.value()?.end;
  }
  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  override writeValue(value: SuiDateRange | null): void {
    const start = value?.start ? startOfDay(value.start) : null;
    const end = value?.end ? startOfDay(value.end) : null;
    this.value.set({ start, end });
    // Open the window on the start month (else end, else today) — unless the
    // consumer pinned an initial month.
    if (!this._initialMonthPinned) {
      this._setFocusedMonth(startOfMonth(start ?? end ?? new Date()));
    }
  }

  onStartChange(date: Date | undefined): void {
    // A start-date change always begins a fresh range — clear any existing end.
    // The primitive's reset emits startDateChange THEN endDateChange; preserving
    // the old end here would emit a transient {newStart, oldEnd} "complete" range,
    // which a controlled consumer treats as a finished selection (breaks the
    // click-1-resets / click-2-completes flow).
    this._commit(date ? startOfDay(date) : null, null);
  }

  onEndChange(date: Date | undefined): void {
    this._commit(this.value()?.start ?? null, date ? startOfDay(date) : null);
    // Field mode: a complete range (both endpoints) commits and closes the popover.
    const v = this.value();
    if (v?.start && v?.end) {
      this._popoverTrigger()?.close();
    }
  }

  onLeftFocusedChange(date: Date): void {
    if (isSameMonth(date, this._focusedMonth())) {
      this.leftFocused.set(date);
    }
  }

  onRightFocusedChange(date: Date): void {
    if (isSameMonth(date, addMonths(this._focusedMonth(), 1))) {
      this.rightFocused.set(date);
    }
  }

  prevMonth(): void {
    this._setFocusedMonth(subMonths(this._focusedMonth(), 1));
  }

  nextMonth(): void {
    this._setFocusedMonth(addMonths(this._focusedMonth(), 1));
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }

  private _commit(start: Date | null, end: Date | null): void {
    this.setValue({ start, end });
  }

  private _setFocusedMonth(month: Date): void {
    const m = startOfMonth(month);
    this._focusedMonth.set(m);
    this.leftFocused.set(m);
    this.rightFocused.set(startOfMonth(addMonths(m, 1)));
  }
}
